/**
 * DocumentosTab — list/create investor-visible documents.
 *
 * Reuses existing endpoints:
 *   GET   /documents      (admin sees all; we filter to this investor)
 *   POST  /documents      (file_url-based; no upload provider in PR #7)
 *   PATCH /documents/{id}
 *   DELETE /documents/{id}
 *
 * UI categories map to the existing backend enum:
 *   Contratos          → contracts
 *   Reportes de obra   → construction
 *   Recibos de pago    → financials
 *   Documentos legales → legal
 *   Presentaciones     → other
 *   Dossier            → other
 *   Otros              → other
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Plus, Trash, FileText, ArrowSquareOut, PencilSimple } from "@phosphor-icons/react";
import VisibilityScopeSelector, { ScopeChip } from "@/components/admin/operaciones/VisibilityScopeSelector";
import DocumentFileInput from "@/components/admin/DocumentFileInput";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const UI_CATEGORIES = [
  { ui: "Contratos",          backend: "contracts" },
  { ui: "Reportes de obra",   backend: "construction" },
  { ui: "Recibos de pago",    backend: "financials" },
  { ui: "Documentos legales", backend: "legal" },
  { ui: "Presentaciones",     backend: "other" },
  { ui: "Dossier",            backend: "other" },
  { ui: "Otros",              backend: "other" },
];

function backendToUiLabel(backendCategory) {
  const hit = UI_CATEGORIES.find((c) => c.backend === backendCategory);
  return hit?.ui || (backendCategory || "Otros");
}

export default function DocumentosTab({ investor, villa, villas = [] }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    title: "",
    ui_category: "Contratos",
    villa_id: "",
    file_url: "",
    description: "",
    document_date: "",
    scope: "investor",
    confirm_project_all: false,
  });
  const [busy, setBusy] = useState(false);
  // Dual-mode source: upload a real file or paste an external URL. Edit flow
  // keeps working on metadata alone; if the admin wants to replace the file,
  // they can create a new version via the existing new-doc flow.
  const [sourceMode, setSourceMode] = useState("file");
  const [file, setFile] = useState(null);

  const resetForm = () => {
    setForm({
      title: "", ui_category: "Contratos", villa_id: "", file_url: "", description: "",
      document_date: "", scope: "investor", confirm_project_all: false,
    });
    setEditId(null);
    setSourceMode("file");
    setFile(null);
  };

  const startEdit = (d) => {
    setEditId(d.doc_id || d.document_id);
    setForm({
      title: d.title || "",
      ui_category: backendToUiLabel(d.category),
      villa_id: d.villa_id || "",
      file_url: d.file_url || "",
      description: d.description || "",
      document_date: d.document_date || "",
      scope: d.visibility_scope || "investor",
      confirm_project_all: false,
    });
    // Preselect the mode that matches the existing doc so the admin can just
    // tweak metadata without re-uploading. Editing the actual file/URL is
    // still available if they change the source mode.
    setSourceMode(d.file_id ? "file" : "url");
    setFile(null);
    setShowForm(true);
  };

  const load = async () => {
    if (!investor) return;
    setLoading(true);
    try {
      const { data } = await api.get("/documents");
      // Show only docs that target this investor — either explicitly
      // (investor / investor_all_villas) or via legacy investor_user_id.
      const mine = (data || []).filter(
        (d) => d.investor_user_id === investor.user_id,
      );
      setDocs(mine);
      setError("");
    } catch (e) {
      setError(formatError(e));
      setDocs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investor?.user_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!investor) return;
    if (!form.title.trim()) {
      setError("El título es obligatorio.");
      return;
    }
    if (!editId) {
      if (sourceMode === "file" && !file) {
        setError("Selecciona un archivo para subir.");
        return;
      }
      if (sourceMode === "url" && !form.file_url.trim()) {
        setError("Introduce la URL del archivo.");
        return;
      }
    }
    if (!form.scope) {
      setError("Selecciona un alcance de visibilidad.");
      return;
    }
    if (form.scope === "villa" && !form.villa_id && !villa?.villa_id) {
      setError("Para el alcance 'Inversores de esta villa' selecciona una villa.");
      return;
    }
    if (form.scope === "project_all" && !form.confirm_project_all) {
      setError("Marca la confirmación explícita para alcance 'Todas las villas / proyecto'.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const cat = UI_CATEGORIES.find((c) => c.ui === form.ui_category) || UI_CATEGORIES[0];
      // Resolve investor_user_id / villa_id from the chosen scope. The
      // backend re-validates and may null fields it doesn't need, but we
      // send the most relevant ones for clarity in audit logs.
      const scope = form.scope;
      const scopedInvestor =
        scope === "investor" || scope === "investor_all_villas" ? investor.user_id : null;
      const scopedVilla =
        scope === "villa" ? (form.villa_id || villa?.villa_id || null) : null;

      if (editId) {
        // PATCH only touches metadata in V1. Admin can replace the actual
        // file by publishing a new document version.
        await api.patch(`/documents/${editId}`, {
          title: form.title.trim(),
          category: cat.backend,
          file_url: sourceMode === "url" ? form.file_url.trim() : undefined,
          description: form.description.trim() || null,
          document_date: form.document_date || null,
          visibility_scope: scope,
          investor_user_id: scopedInvestor,
          villa_id: scopedVilla,
          confirm_project_all: scope === "project_all" ? !!form.confirm_project_all : undefined,
        });
      } else {
        const payload = {
          title: form.title.trim(),
          doc_type: "general",
          category: cat.backend,
          visibility_scope: scope,
          investor_user_id: scopedInvestor,
          villa_id: scopedVilla,
          description: form.description.trim() || null,
          document_date: form.document_date || null,
          confirm_project_all: scope === "project_all" ? !!form.confirm_project_all : undefined,
        };
        if (sourceMode === "file") {
          payload.file_base64 = file.base64;
          payload.file_filename = file.filename;
          payload.file_mime = file.mime;
        } else {
          payload.file_url = form.file_url.trim();
        }
        await api.post("/documents", payload);
      }
      resetForm();
      setShowForm(false);
      await load();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`¿Eliminar el documento "${doc.title}"?`)) return;
    try {
      await api.delete(`/documents/${doc.doc_id || doc.document_id}`);
      await load();
    } catch (e) {
      setError(formatError(e));
    }
  };

  const docsByCategory = useMemo(() => {
    const map = {};
    docs.forEach((d) => {
      const label = backendToUiLabel(d.category);
      (map[label] = map[label] || []).push(d);
    });
    return map;
  }, [docs]);

  if (!investor) return null;

  return (
    <div data-testid="operaciones-tab-documentos">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-stone-600">
          {docs.length} {docs.length === 1 ? "documento" : "documentos"} visibles para{" "}
          <span className="font-medium">{investor.name || investor.email}</span>
        </div>
        <button
          className="dv-btn dv-btn-primary text-sm"
          onClick={() => {
            if (showForm) { resetForm(); setShowForm(false); }
            else { resetForm(); setShowForm(true); }
          }}
          data-testid="documentos-create-toggle"
        >
          <Plus size={14} /> {showForm ? "Cancelar" : "Nuevo documento"}
        </button>
      </div>

      {error && (
        <div className="mb-3 text-sm px-3 py-2 rounded-md border bg-red-50 border-red-200 text-red-800">
          {error}
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="dv-card mb-4 grid sm:grid-cols-2 gap-3" data-testid="documentos-form">
          <input
            className="dv-input sm:col-span-2"
            placeholder="Título del documento"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
            data-testid="documentos-form-title"
          />
          <select
            className="dv-input"
            value={form.ui_category}
            onChange={(e) => setForm({ ...form, ui_category: e.target.value })}
            data-testid="documentos-form-category"
          >
            {UI_CATEGORIES.map((c) => (
              <option key={c.ui} value={c.ui}>{c.ui}</option>
            ))}
          </select>
          <div className="sm:col-span-2">
            <VisibilityScopeSelector
              value={form.scope}
              onChange={(scope) => setForm({ ...form, scope })}
              confirmProjectAll={form.confirm_project_all}
              onConfirmProjectAll={(v) => setForm({ ...form, confirm_project_all: v })}
              testidPrefix="documentos-form"
            />
          </div>
          {form.scope === "villa" && (
            <select
              className="dv-input sm:col-span-2"
              value={form.villa_id}
              onChange={(e) => setForm({ ...form, villa_id: e.target.value })}
              data-testid="documentos-form-villa"
              required
            >
              <option value="">— Selecciona la villa para el alcance —</option>
              {villas.map((v) => (
                <option key={v.villa_id} value={v.villa_id}>{v.name || v.villa_id}</option>
              ))}
            </select>
          )}
          <DocumentFileInput
            mode={sourceMode}
            onModeChange={setSourceMode}
            url={form.file_url}
            onUrlChange={(v) => setForm({ ...form, file_url: v })}
            file={file}
            onFileChange={setFile}
            onError={(msg) => setError(msg)}
            testidPrefix="documentos-form-source"
          />
          <textarea
            className="dv-input sm:col-span-2"
            placeholder="Descripción (opcional)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2}
          />
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-widest text-stone-500">
              Fecha del documento (opcional)
            </label>
            <input
              type="date"
              className="dv-input mt-1"
              value={form.document_date}
              onChange={(e) => setForm({ ...form, document_date: e.target.value })}
              data-testid="documentos-form-document-date"
            />
            <div className="text-[11px] text-stone-500 mt-1">
              Si dejas vacío se muestra la fecha de subida.
            </div>
          </div>
          <div className="sm:col-span-2 text-xs text-stone-500">
            {editId
              ? "Al editar, si cambias el archivo o la URL se guardará el nuevo enlace; si no tocas nada, se conserva el actual."
              : "El inversor verá el documento según el alcance elegido."}
          </div>
          <div className="sm:col-span-2 flex gap-2">
            <button disabled={busy} className="dv-btn dv-btn-primary flex-1" data-testid="documentos-form-submit">
              {busy ? "Guardando…" : (editId ? "Guardar cambios" : "Crear documento")}
            </button>
            <button type="button" onClick={() => { resetForm(); setShowForm(false); }} className="dv-btn dv-btn-ghost">Cancelar</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="dv-card text-center py-6 text-stone-500 text-sm">Cargando…</div>
      ) : docs.length === 0 ? (
        <div className="dv-card text-center py-8 text-stone-500 text-sm">
          Este inversor todavía no tiene documentos asignados.
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(docsByCategory).map(([cat, list]) => (
            <div key={cat} className="dv-card p-0 overflow-hidden">
              <div className="px-4 py-2 bg-stone-50 border-b border-stone-200 text-xs uppercase tracking-wider text-stone-500">
                {cat}
              </div>
              <ul>
                {list.map((d) => (
                  <li
                    key={d.doc_id || d.document_id}
                    className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-stone-100 last:border-b-0"
                    data-testid={`documentos-row-${d.doc_id || d.document_id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText size={16} className="text-stone-400 shrink-0" />
                      <ScopeChip scope={d.visibility_scope} />
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{d.title}</div>
                        {d.description && (
                          <div className="text-xs text-stone-500 truncate">{d.description}</div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {(d.file_id || d.file_url) && (
                        <a
                          href={d.file_id ? `${API_BASE}/api/files/${d.file_id}` : d.file_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs px-2 py-1 rounded border border-stone-300 text-stone-700 hover:bg-stone-100 inline-flex items-center gap-1"
                        >
                          <ArrowSquareOut size={12} /> Abrir
                        </a>
                      )}
                      <button
                        onClick={() => startEdit(d)}
                        className="text-xs px-2 py-1 rounded border border-stone-300 text-stone-700 hover:bg-stone-100 inline-flex items-center gap-1"
                        data-testid={`documentos-edit-${d.doc_id || d.document_id}`}
                      >
                        <PencilSimple size={12} /> Editar
                      </button>
                      <button
                        onClick={() => remove(d)}
                        className="text-xs px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50 inline-flex items-center gap-1"
                        data-testid={`documentos-delete-${d.doc_id || d.document_id}`}
                      >
                        <Trash size={12} /> Eliminar
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
